import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  TextField,
  Typography,
  CircularProgress,
  Alert
} from '@mui/material';
import { CloudUpload, Description, Edit } from '@mui/icons-material';
import { useLazyQuery, useMutation } from '@apollo/client';
import { gql } from '@apollo/client';
import myClassroomClient from 'utils/graphql/myclassroom';

// GraphQL queries and mutations
const GET_SIGNED_UPLOAD_URL = gql`
  query GetSignedUploadUrl($filename: String!, $fileType: String!, $fileSize: Float!, $feature: String!) {
    getSignedUploadUrl(
      filename: $filename
      fileType: $fileType
      fileSize: $fileSize
      feature: $feature
    ) {
      signedUrl
      filename
      expiresAt
    }
  }
`;

const CONFIRM_FILE_UPLOAD = gql`
  mutation ConfirmFileUpload($filename: String!) {
    confirmFileUpload(filename: $filename) {
      ok
      filename
    }
  }
`;

const GET_SIGNED_DOWNLOAD_URL = gql`
  query GetSignedDownloadUrl($filename: String!) {
    getSignedDownloadUrl(filename: $filename) {
      signedUrl
      filename
      expiresAt
    }
  }
`;

export const SyllabusUpload = ({ onFileUpload,initialFileName }) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [editableFileName, setEditableFileName] = useState<string>('');
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [uploadedFileName, setUploadedFileName] = useState<string | null>(initialFileName);

  // GraphQL hooks
  // const [getSignedUploadUrl] = useLazyQuery(GET_SIGNED_UPLOAD_URL);

  const [getSignedUploadUrl, { loading: loadingGetSigUrl, error: errorGetSigUrl, data: dataGetSigUrl }] =
    useLazyQuery(GET_SIGNED_UPLOAD_URL, {
      client: myClassroomClient,
      fetchPolicy: "no-cache",
    });

  const [confirmFileUpload, { loading:loadingConFileUp, error:errorConFileUp, data:dataConFileUp }] = useMutation(
    CONFIRM_FILE_UPLOAD,
    {
      client: myClassroomClient,
    }
  );
  // const [getSignedDownloadUrl] = useLazyQuery(GET_SIGNED_DOWNLOAD_URL);

    const [getSignedDownloadUrl, { loading: loadingGetSigDowUrl, error: errorGetSigDowUrl, data: dataGetSigDowUrl }] =
    useLazyQuery(GET_SIGNED_DOWNLOAD_URL, {
      client: myClassroomClient,
      fetchPolicy: "no-cache",
    });

  const handleFileChange = async (event) => {
    const file = event.target.files[0];
    
    if (!file) return;

    // Validate file
    if (file.type !== 'application/pdf') {
      setUploadError('Please select a PDF file');
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      setUploadError('File size must be under 10MB');
      return;
    }

    setSelectedFile(file);
    setEditableFileName(file.name.replace('.pdf', ''));
    setUploadError(null);
  };

  const handleUpload = async () => {
    if (!selectedFile || !editableFileName.trim()) {
      setUploadError('Please select a file and provide a filename');
      return;
    }

    setIsUploading(true);
    setUploadError(null);

    try {
      // Step 1: Get signed upload URL
      const filename = `${editableFileName.trim()}.pdf`;
      const { data: uploadUrlData } = await getSignedUploadUrl({
        variables: {
          filename: filename,
          fileType: 'application/pdf',
          fileSize: selectedFile.size,
          feature: 'exam-syllabus'
        }
      });

      if (!uploadUrlData?.getSignedUploadUrl?.signedUrl) {
        throw new Error('Failed to get upload URL');
      }

      // Step 2: Upload file to signed URL
      const uploadResponse = await fetch(uploadUrlData.getSignedUploadUrl.signedUrl, {
        method: 'PUT',
        body: selectedFile,
        headers: {
          'Content-Type': 'application/pdf',
        },
      });

      if (!uploadResponse.ok) {
        throw new Error('Failed to upload file to storage');
      }

      // Step 3: Confirm file upload
      const { data: confirmData } = await confirmFileUpload({
        variables: {
          filename: uploadUrlData.getSignedUploadUrl.filename
        }
      });

      if (!confirmData?.confirmFileUpload?.ok) {
        throw new Error('Failed to confirm file upload');
      }

      // Success
      setUploadedFileName(confirmData.confirmFileUpload.filename);
      
      // Call the parent callback
      onFileUpload({
        file: selectedFile,
        filename: confirmData.confirmFileUpload.filename,
        originalName: editableFileName.trim()
      });

      setUploadError(null);

    } catch (error:any) {
      setUploadError(error.message || 'Failed to upload file');
    } finally {
      setIsUploading(false);
    }
  };

  const handleFileNameChange = (event) => {
    setEditableFileName(event.target.value);
  };

  const getDownloadUrl = async () => {
    if (!uploadedFileName) return;

    try {
      const { data } = await getSignedDownloadUrl({
        variables: { filename: uploadedFileName }
      });

      if (data?.getSignedDownloadUrl?.signedUrl) {
        window.open(data.getSignedDownloadUrl.signedUrl, '_blank');
      }
    } catch (error) {
      setUploadError('Failed to get download URL');
    }
  };
  useEffect(()=>{
    if(uploadedFileName !== initialFileName ){
       setUploadedFileName(initialFileName);
    }
  },[initialFileName])

  return (
    <Box sx={{ mb: 3 }}>
      {/* File Selection */}
      <input
        accept="application/pdf"
        style={{ display: 'none' }}
        id="syllabus-upload"
        type="file"
        onChange={handleFileChange}
      />
      <label htmlFor="syllabus-upload">
        <Button
          variant="outlined"
          component="span"
          startIcon={<CloudUpload />}
          disabled={isUploading}
          sx={{
            borderStyle: 'dashed',
            borderWidth: 2,
            borderColor: '#1976d2',
            color: '#1976d2',
            padding: '12px 24px',
            textTransform: 'none'
          }}
        >
          {selectedFile ? 'Change File' : 'Upload Syllabus'}
        </Button>
      </label>
      
      <Typography variant="caption" display="block" sx={{ mt: 1, color: 'text.secondary' }}>
        Format: PDF, Max Size: 10 MB
      </Typography>

      {/* Error Display */}
      {uploadError && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {uploadError}
        </Alert>
      )}

      {/* Selected File with Editable Name */}
      {selectedFile && (
        <Box sx={{ mt: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <Description color="primary" />
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              Selected: {selectedFile.name}
            </Typography>
          </Box>
          
          <TextField
            fullWidth
            label="File Name"
            value={editableFileName}
            onChange={handleFileNameChange}
            placeholder="Enter file name"
            size="small"
            sx={{ mb: 2 }}
            InputProps={{
              endAdornment: <Typography variant="body2" sx={{ color: 'text.secondary' }}>.pdf</Typography>
            }}
          />

          <Button
            variant="contained"
            onClick={handleUpload}
            disabled={isUploading || !editableFileName.trim()}
            startIcon={isUploading ? <CircularProgress size={16} /> : <CloudUpload />}
          >
            {isUploading ? 'Uploading...' : 'Upload File'}
          </Button>
        </Box>
      )}

      {/* Successfully Uploaded File */}
      {uploadedFileName && (
        <Box sx={{ mt: 2, p: 2, bgcolor: 'success.light', borderRadius: 1 }}>
          <Typography variant="body2" color="success.dark" sx={{ mb: 1 }}>
            ✓ File uploaded successfully!
          </Typography>
          <Typography variant="caption" display="block" sx={{ color: 'text.secondary' }}>
            Filename: {uploadedFileName}
          </Typography>
          <Button
            size="small"
            variant="outlined"
            onClick={getDownloadUrl}
            sx={{ mt: 1 }}
          >
            Download
          </Button>
        </Box>
      )}
    </Box>
  );
};